import React, { useState, useEffect } from 'react';
import { Container, Typography, Checkbox, FormControlLabel, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Chip, Box, useTheme, useMediaQuery, Card, CardContent, CardActions, Stack, Divider, IconButton, Snackbar } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbarFilterButton, GridToolbarExport, GridToolbarColumnsButton, GridToolbarDensitySelector, Toolbar } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { LicenseResponseDto, LicenseStatus } from '../apiClient/Api';
import { useApplicationContext } from 'ApplicationContext';
import dayjs from 'dayjs';
import { useParams, Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faQrcode, faUpload } from '@fortawesome/free-solid-svg-icons';
import QRCode from 'react-qr-code';

const LicenseRequests: React.FC = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<LicenseResponseDto[]>([]);
  const [showUnapprovedOnly, setShowUnapprovedOnly] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const context = useApplicationContext();
  const { supportId } = useParams<{ supportId?: string }>(); // Optional supportCode from URL
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [selectedLicenseCode, setSelectedLicenseCode] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequests = async () => {
      if (supportId) {
        // Fetch licenses for specific support contract
        const response = await context.api.service.serviceGetAllRequestedLicenses();
        setRequests(response.data);
      } else {
        // Fetch all user's licenses
        const response = await context.api.service.serviceGetAllRequestedLicenses();
        setRequests(response.data);
      }
    };

    fetchRequests();
  }, [supportId]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowUnapprovedOnly(event.target.checked);
  };

  /**
   * Handles ordering a license.
   * @param {string} licenseId - The ID of the license to order.
   */
  const refetchLicenses = async () => {
    if (supportId) {
      const response = await context.api.service.serviceGetAllRequestedLicenses();
      setRequests(response.data);
    } else {
      const response = await context.api.service.serviceGetAllRequestedLicenses();
      setRequests(response.data);
    }
  };

  const handleOrder = async (licenseId: string) => {
    try {
      await context.api.service.serviceOrderLicense(licenseId);
      await refetchLicenses();
      setErrorMessage(null);
    } catch (error: any) {
      console.error("Failed to order license:", error);
      if (error.response?.status === 401) {
        setErrorMessage(error.response.data.message || t('licenseOverview.unauthorizedError'));
      }
    }
  };

  /**
   * Handles revoking a license.
   * @param {string} licenseId - The ID of the license to revoke.
   */
  const handleRevoke = async (licenseId: string) => {
    try {
      await context.api.service.serviceRevokeLicense(licenseId);
      await refetchLicenses();
      setErrorMessage(null);
    } catch (error: any) {
      console.error("Failed to revoke license:", error);
      if (error.response?.status === 401) {
        setErrorMessage(error.response.data.message || t('licenseOverview.unauthorizedError'));
      }
    }
  };

  /**
   * Handles deleting a license.
   * @param {string} licenseId - The ID of the license to delete.
   */
  const handleDelete = async (licenseId: string) => {
    try {
      await context.api.service.serviceDeleteLicense(licenseId);
      await refetchLicenses();
      setErrorMessage(null);
    } catch (error: any) {
      console.error("Failed to delete license:", error);
      if (error.response?.status === 401) {
        setErrorMessage(error.response.data.message || t('licenseOverview.unauthorizedError'));
      }
    }
  };

  /**
   * Copies text to clipboard and shows confirmation
   */
  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  /**
   * Handles calling the callback URL to transfer the license to the server.
   * @param {string} callbackUrl - The base64 encoded callback URL.
   * @param {string} licenseCode - The license code to transfer.
   */
  const handleTransferLicense = (callbackUrl: string, licenseCode: string) => {
    try {
      const decodedUrl = atob(callbackUrl);
      const url = new URL(decodedUrl);
      url.searchParams.set('licenseId', licenseCode);
      window.open(url.toString(), '_blank');
    } catch (error) {
      console.error('Failed to open callback URL:', error);
    }
  };

  /**
   * Orders all licenses that are in the cart.
   * Handles unauthorized access error (401)
   */
  const orderAllLicenses = async () => {
    try {
      const inCartIds = requests
        .filter(license => license.status === LicenseStatus.InCart)
        .map(license => license.id)
        .filter(l => l != undefined) as string[];

      if (inCartIds.length > 0) {
        await context.api.service.serviceOrderLicenses(inCartIds);
        console.log("All licenses ordered successfully.");
        setOpenDialog(false);
        await refetchLicenses();
        setErrorMessage(null);
      } else {
        console.log("No licenses in cart to order.");
      }
    } catch (error: any) {
      console.error("Failed to order licenses:", error);
      if (error.response?.status === 401) {
        setErrorMessage(error.response.data.message);
      }
    }
  };

  const filteredRequests = requests.filter(request => {
    // Filter out revoked licenses if not explicitly shown
    if (!showRevoked && request.status === LicenseStatus.Revoked) {
      return false;
    }
    // Filter for unapproved only if checkbox is checked
    if (showUnapprovedOnly && request.licenseGeneratedAt) {
      return false;
    }
    return true;
  });

  /**
   * Handles opening the QR code dialog
   */
  const handleShowQrCode = (licenseCode: string) => {
    setSelectedLicenseCode(licenseCode);
    setQrCodeDialogOpen(true);
  };

  /**
   * Gets the translated status text based on the request status.
   */
  const getStatusText = (status: LicenseStatus): string => {
    switch (status) {
      case LicenseStatus.InCart:
        return t('licenseOverview.statusInCart');
      case LicenseStatus.Ordered:
        return t('licenseOverview.statusOrdered');
      case LicenseStatus.LicenseGenerated:
        return t('licenseOverview.statusGenerated');
      case LicenseStatus.Revoked:
        return t('licenseOverview.statusRevoked');
      case LicenseStatus.Rejected:
        return t('licenseOverview.rejected');
      default:
        return '-';
    }
  };

  const CustomToolbar = () => {
    return (
      <Toolbar style={{ justifyContent: 'flex-start', gap: 8 }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </Toolbar>
    );
  };

  /**
   * Renders mobile card view for licenses
   */
  const renderMobileView = () => {
    if (filteredRequests.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {t('licenseOverview.noLicenses') || 'Keine Lizenzen gefunden'}
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2} sx={{ px: 2 }}>
        {filteredRequests.map((request) => (
          <Card key={request.id}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    label={getStatusText(request.status!)}
                    size="small"
                    color={
                      request.status === LicenseStatus.LicenseGenerated ? 'success' :
                      request.status === LicenseStatus.Ordered ? 'warning' :
                      request.status === LicenseStatus.Revoked ? 'error' :
                      request.status === LicenseStatus.InCart ? 'info' :
                      'default'
                    }
                  />
                  {request.tagName && (
                    <Chip label={request.tagName} size="small" />
                  )}
                </Stack>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {request.fileTypeName}
                  </Typography>
                  {request.description && (
                    <Typography variant="body2" color="text.secondary">
                      {request.description}
                    </Typography>
                  )}
                </Box>

                <Divider />

                <Stack spacing={1}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('licenseOverview.requestId')}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
                      {request.hostId}
                    </Typography>
                  </Stack>

                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('licenseOverview.userName')}:
                    </Typography>
                    <Typography variant="body2">
                      {request.userName}
                    </Typography>
                  </Stack>

                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('licenseOverview.supportCode')}:
                    </Typography>
                    <Typography variant="body2">
                      {request.supportCodeId}
                    </Typography>
                  </Stack>

                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('licenseOverview.requestedAt')}:
                    </Typography>
                    <Typography variant="body2">
                      {dayjs(request.requestedAt).format('DD.MM.YYYY')}
                    </Typography>
                  </Stack>

                  {request.licenseGeneratedAt && (
                    <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('licenseOverview.licenseGeneratedAt')}:
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(request.licenseGeneratedAt).format('DD.MM.YYYY')}
                      </Typography>
                    </Stack>
                  )}

                  {request.licenseCode && (
                    <>
                      <Divider />
                      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('licenseOverview.licenseCode')}:
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
                            {request.licenseCode}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyToClipboard(request.licenseCode!)}
                            color="primary"
                          >
                            <FontAwesomeIcon icon={faCopy} size="xs" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleShowQrCode(request.licenseCode!)}
                            color="primary"
                          >
                            <FontAwesomeIcon icon={faQrcode} size="xs" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Stack>
            </CardContent>
            {request.status !== LicenseStatus.LicenseGenerated && request.status !== LicenseStatus.Rejected && (
              <CardActions>
                <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end' }}>
                  {request.status === LicenseStatus.Ordered && (
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => handleRevoke(request.id!)}
                    >
                      {t('licenseOverview.revoke')}
                    </Button>
                  )}
                  {request.status === LicenseStatus.Revoked && (
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => handleDelete(request.id!)}
                    >
                      {t('licenseOverview.delete')}
                    </Button>
                  )}
                  {request.status === LicenseStatus.InCart && (
                    <>
                      <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={() => handleOrder(request.id!)}
                      >
                        {t('licenseOverview.order')}
                      </Button>
                      <Button
                        size="small"
                        color="secondary"
                        variant="contained"
                        onClick={() => handleDelete(request.id!)}
                      >
                        {t('licenseOverview.delete')}
                      </Button>
                    </>
                  )}
                </Stack>
              </CardActions>
            )}
          </Card>
        ))}
      </Stack>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: t('licenseOverview.status'),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={getStatusText(params.value as LicenseStatus)} 
          size="small"
          color={
            params.value === LicenseStatus.LicenseGenerated ? 'success' :
            params.value === LicenseStatus.Ordered ? 'warning' :
            params.value === LicenseStatus.Revoked ? 'error' :
            params.value === LicenseStatus.InCart ? 'info' :
            'default'
          }
        />
      )
    },
    {
      field: 'hostId',
      headerName: t('licenseOverview.requestId'),
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'userName',
      headerName: t('licenseOverview.userName'),
      width: 150,
      flex: 0.5
    },
    {
      field: 'supportCodeId',
      headerName: t('licenseOverview.supportCode'),
      width: 120
    },
    {
      field: 'fileTypeName',
      headerName: t('licenseOverview.application'),
      width: 180,
      flex: 0.8
    },
    {
      field: 'tagName',
      headerName: t('licenseOverview.variant'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? (
          <Chip label={params.value} size="small" />
        ) : null
    },
    {
      field: 'description',
      headerName: t('licenseOverview.description'),
      width: 250,
      flex: 1
    },
    {
      field: 'requestedAt',
      headerName: t('licenseOverview.requestedAt'),
      width: 110,
      valueFormatter: (value) => dayjs(value).format('DD.MM.YYYY')
    },
    {
      field: 'licenseGeneratedAt',
      headerName: t('licenseOverview.licenseGeneratedAt'),
      width: 120,
      valueFormatter: (value) => value ? dayjs(value).format('DD.MM.YYYY') : '-'
    },
    {
      field: 'licenseCode',
      headerName: t('licenseOverview.licenseCode'),
      width: 320,
      renderCell: (params: GridRenderCellParams) => {
        const request = params.row as LicenseResponseDto;
        return params.value ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
              {params.value}
            </Typography>
            {request.callbackUrl && (
              <IconButton
                size="small"
                onClick={() => handleTransferLicense(request.callbackUrl!, params.value)}
                color="primary"
              >
                <FontAwesomeIcon icon={faUpload} size="xs" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => handleCopyToClipboard(params.value)}
              color="primary"
            >
              <FontAwesomeIcon icon={faCopy} size="xs" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleShowQrCode(params.value)}
              color="primary"
            >
              <FontAwesomeIcon icon={faQrcode} size="xs" />
            </IconButton>
          </Stack>
        ) : null;
      }
    },
    {
      field: 'actions',
      headerName: t('licenseOverview.actions'),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const request = params.row as LicenseResponseDto;
        
        if (request.status === LicenseStatus.LicenseGenerated) {
          return null;
        }
        
        if (request.status === LicenseStatus.Ordered) {
          return (
            <Button 
              size="small" 
              color='warning' 
              onClick={() => handleRevoke(request.id!)}
            >
              {t('licenseOverview.revoke')}
            </Button>
          );
        }
        
        if (request.status === LicenseStatus.Revoked) {
          return (
            <Button 
              size="small" 
              color='secondary' 
              onClick={() => handleDelete(request.id!)}
            >
              {t('licenseOverview.delete')}
            </Button>
          );
        }
        
        if (request.status === LicenseStatus.Rejected) {
          return (
            <Typography variant="body2" color="error">
              {t('licenseOverview.rejected')}
            </Typography>
          );
        }
        
        if (request.status === LicenseStatus.InCart) {
          return (
            <ButtonGroup size="small" variant='contained'>
              <Button color='primary' onClick={() => handleOrder(request.id!)}>
                {t('licenseOverview.order')}
              </Button>
              <Button color='secondary' onClick={() => handleDelete(request.id!)}>
                {t('licenseOverview.delete')}
              </Button>
            </ButtonGroup>
          );
        }
        
        return null;
      }
    }
  ];

  return (
    <Container maxWidth={false}>
      {errorMessage && (
        <Typography color="error" sx={{ mt: 2, mb: 2 }}>
          {errorMessage}
        </Typography>
      )}
      <Typography variant="h4" gutterBottom>
        {supportId ? t('licenseOverview.title') : t('licenseOverview.allLicenses')}
      </Typography>
      {supportId && (
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          {t('licenseOverview.supportContract')}: {supportId}
        </Typography>
      )}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={showUnapprovedOnly} onChange={handleFilterChange} />}
          label={t('licenseOverview.showUnapprovedOnly')}
        />
        <FormControlLabel
          control={<Checkbox checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />}
          label={t('licenseRequest.showRevoked')}
        />
        {supportId && (
          <Button 
            component={Link} 
            to={`/licenses/${supportId}/request`} 
            variant="contained" 
            color="primary"
            sx={{ ml: 2 }}
          >
            {t('licenseOverview.requestNewLicense')}
          </Button>
        )}
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => setOpenDialog(true)}
          sx={{ ml: 1 }}
        >
          {t('licenseRequest.orderAll')}
        </Button>
      </Box>
      
      {isMobile ? (
        renderMobileView()
      ) : (
        <DataGrid
          rows={filteredRequests}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          showToolbar
          slots={{
            toolbar: CustomToolbar
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 }
            },
            sorting: {
              sortModel: [{ field: 'requestedAt', sort: 'desc' }]
            }
          }}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="order-all-dialog-title"
        aria-describedby="order-all-dialog-description"
      >
        <DialogTitle id="order-all-dialog-title">
          {t('licenseRequest.orderAll')}
        </DialogTitle>
        <DialogContent>
          {errorMessage ? (
            <DialogContentText color="error">
              {errorMessage}
            </DialogContentText>
          ) : (
            <>
              <DialogContentText id="order-all-dialog-description">
                {t('licenseGenerator.confirmOrderAllText')}
              </DialogContentText>
              <ul>
                {requests.filter(request => request.status === LicenseStatus.InCart).map(request => (
                  <li key={request.id}>{request.description}</li>
                ))}
              </ul>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            {t('licenseGenerator.cancel')}
          </Button>
          {!errorMessage && (
            <Button onClick={orderAllLicenses} color="primary" autoFocus>
              {t('licenseGenerator.confirm')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={qrCodeDialogOpen}
        onClose={() => setQrCodeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('licenseOverview.qrCodeTitle') || 'Lizenzcode als QR-Code'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ alignItems: 'center', py: 2 }}>
            {selectedLicenseCode && (
              <>
                <Box sx={{ backgroundColor: 'white', padding: 2, borderRadius: 1 }}>
                  <QRCode value={selectedLicenseCode} size={256} level="M" />
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', textAlign: 'center', wordBreak: 'break-all', lineHeight: 'inherit' }}>
                  {selectedLicenseCode}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {t('licenseOverview.qrCodeInfo') || 'Scanne den QR-Code mit deiner Navigator-App. Der Code wird als reiner Text erkannt.'}
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrCodeDialogOpen(false)} color="primary">
            {t('common.close') || 'Schließen'}
          </Button>
          <Button 
            onClick={() => {
              if (selectedLicenseCode) {
                handleCopyToClipboard(selectedLicenseCode);
              }
            }} 
            color="primary"
          >
            <FontAwesomeIcon icon={faCopy} style={{ marginRight: 8 }} />
            {t('common.copy') || 'Kopieren'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={t('licenseOverview.copiedToClipboard') || 'In Zwischenablage kopiert'}
      />
    </Container>
  );
};

export default LicenseRequests;